import React, { useRef, useCallback, useState } from "react";
import {
  Upload,
  Typography,
  notification,
  Spin,
  Alert,
  Button,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import ScheduleViewer from "../components/schedule/ScheduleViewer";
import {
  parseXerFile,
  SCHEDULE_ACCEPTED_EXTENSIONS,
  isXerFile,
  isMppFile,
  serializeScheduleForUpload,
  rehydrateSchedule,
} from "../utils/xerParser";
import {
  fetchProjectSchedule,
  fetchProjectScheduleContent,
  uploadProjectSchedule,
  parseProjectSchedule,
  deleteProjectSchedule,
  downloadProjectSchedule,
} from "../services/scheduleAPI";
import { getApiErrorMessage } from "../utils/apiError";

const { Dragger } = Upload;
const { Text } = Typography;

const Schedule = () => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const projectName = selectedProject?.project_name;
  const queryClient = useQueryClient();
  const uploadRef = useRef(null);
  const [localPreview, setLocalPreview] = useState(null);
  const [localFileName, setLocalFileName] = useState("");
  const [isProcessingFile, setIsProcessingFile] = useState(false);

  const acceptedTypes = SCHEDULE_ACCEPTED_EXTENSIONS.join(",");

  const {
    data: savedSchedule,
    isLoading: isLoadingMeta,
    isError: isMetaError,
    error: metaError,
  } = useQuery({
    queryKey: ["projectSchedule", projectId],
    queryFn: () => fetchProjectSchedule(projectId),
    enabled: !!projectId,
    retry: 1,
  });

  const savedIsXer = isXerFile(savedSchedule?.file_name || "");

  const {
    data: parsedSchedule,
    isLoading: isLoadingContent,
    isError: isContentError,
    error: contentError,
  } = useQuery({
    queryKey: ["projectScheduleContent", projectId, savedSchedule?.file_name],
    queryFn: async () => {
      const content = await fetchProjectScheduleContent(projectId);
      try {
        const preview = parseXerFile(content.content);
        return { preview, fileName: content.fileName };
      } catch (parseError) {
        const message =
          parseError?.message || "The saved XER file could not be parsed.";
        throw new Error(message);
      }
    },
    enabled: !!projectId && !!savedSchedule?.is_previewable && savedIsXer,
    retry: 1,
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ uploadFile, preview, fileName }) => {
      const meta = preview ? serializeScheduleForUpload(preview) : {};
      const result = await uploadProjectSchedule(projectId, uploadFile, meta);
      const resolvedPreview =
        preview ||
        (result.preview_data ? rehydrateSchedule(result.preview_data) : null);
      return { preview: resolvedPreview, fileName };
    },
    onSuccess: ({ preview, fileName }) => {
      setLocalPreview(preview);
      setLocalFileName(fileName);
      queryClient.invalidateQueries({ queryKey: ["projectSchedule", projectId] });
      queryClient.invalidateQueries({
        queryKey: ["projectScheduleContent", projectId],
      });
      notification.success({
        message: "Schedule saved",
        description: preview
          ? `${preview.stats.taskCount} tasks from ${fileName}`
          : `${fileName} stored on this project.`,
      });
    },
    onError: (error) => {
      setLocalPreview(null);
      setLocalFileName("");
      notification.error({
        message: "Could not save to server",
        description: getApiErrorMessage(
          error,
          "Try again when the connection is restored."
        ),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteProjectSchedule(projectId),
    onSuccess: () => {
      setLocalPreview(null);
      setLocalFileName("");
      queryClient.invalidateQueries({ queryKey: ["projectSchedule", projectId] });
      queryClient.removeQueries({
        queryKey: ["projectScheduleContent", projectId],
      });
      notification.success({ message: "Schedule removed" });
    },
    onError: (error) => {
      notification.error({
        message: "Delete failed",
        description: getApiErrorMessage(error, "Could not delete schedule"),
      });
    },
  });

  const processFile = useCallback(
    async (file) => {
      if (!projectId || !file) return;

      setIsProcessingFile(true);
      try {
        const buffer = await file.arrayBuffer();
        const uploadFile = new File([buffer], file.name, {
          type: file.type || "application/octet-stream",
        });

        let preview = null;
        setLocalPreview(null);
        setLocalFileName(file.name);

        if (isXerFile(file.name)) {
          const content = new TextDecoder().decode(buffer);
          preview = parseXerFile(content);
        } else if (isMppFile(file.name)) {
          try {
            const parsed = await parseProjectSchedule(projectId, uploadFile);
            preview = rehydrateSchedule(parsed.preview);
          } catch (parseError) {
            notification.warning({
              message: "MPP preview unavailable",
              description: getApiErrorMessage(
                parseError,
                "The file will still be uploaded, but could not be previewed."
              ),
            });
          }
        }

        uploadMutation.mutate({ uploadFile, preview, fileName: file.name });
      } catch (error) {
        notification.error({
          message: "Could not read file",
          description: getApiErrorMessage(
            error,
            "The selected file could not be processed."
          ),
        });
      } finally {
        setIsProcessingFile(false);
      }
    },
    [projectId, uploadMutation]
  );

  const handleFile = useCallback(
    (file) => {
      processFile(file);
      return false;
    },
    [processFile]
  );

  const openUpload = () => uploadRef.current?.click();

  const handleDownload = () => {
    const name = localFileName || savedSchedule?.file_name;
    if (!name) return;
    downloadProjectSchedule(projectId, name);
  };

  const handleDelete = () => {
    if (savedSchedule) {
      deleteMutation.mutate();
    } else {
      setLocalPreview(null);
      setLocalFileName("");
    }
  };

  const savedMppPreview =
    savedSchedule?.preview_data && isMppFile(savedSchedule.file_name || "")
      ? rehydrateSchedule(savedSchedule.preview_data)
      : null;

  const isLoading =
    isLoadingMeta ||
    (savedSchedule?.is_previewable && savedIsXer && isLoadingContent && !localPreview);
  const activePreview = localPreview || parsedSchedule?.preview || savedMppPreview;
  const activeFileName =
    localFileName || parsedSchedule?.fileName || savedSchedule?.file_name || "";
  const hasStoredOnly =
    !activePreview && savedSchedule && !savedSchedule.is_previewable;
  const isBusy = isProcessingFile || uploadMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center py-1 pb-8">
        <Spin size="large" tip="Loading project schedule..." />
      </div>
    );
  }

  if (activePreview) {
    return (
      <>
        <ScheduleViewer
          schedule={activePreview}
          fileName={activeFileName}
          projectName={projectName}
          onUploadClick={openUpload}
          onDownload={handleDownload}
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
          isUploading={isBusy}
        />
        <input
          ref={uploadRef}
          type="file"
          accept={acceptedTypes}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) processFile(file);
            event.target.value = "";
          }}
        />
      </>
    );
  }

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold mb-4">Schedule</h1>
        <p className="mb-4 text-gray-600">
          Upload a schedule file for {projectName || "this project"}. MPP and XER
          files open in the Gantt viewer.
        </p>

        {(isMetaError || isContentError) && (
          <Alert
            type="warning"
            showIcon
            message="Saved schedule could not be loaded from the server"
            description={getApiErrorMessage(
              metaError || contentError,
              "You can still upload a new schedule file below."
            )}
            className="mb-4"
          />
        )}

        {hasStoredOnly && (
          <Alert
            type="info"
            showIcon
            message={`${savedSchedule.file_name} is stored on this project`}
            description="This format could not be previewed in the Gantt viewer. Try MPP or XER instead."
            action={
              <Button type="link" onClick={handleDownload}>
                Download
              </Button>
            }
            className="mb-4"
          />
        )}

        <Dragger
          accept={acceptedTypes}
          multiple={false}
          showUploadList={false}
          beforeUpload={handleFile}
          disabled={isBusy}
        >
          {isBusy ? (
            <div className="flex flex-col items-center gap-2 py-4">
              <Spin size="large" />
              <p className="m-0 font-medium">
                {isMppFile(localFileName)
                  ? "Parsing MPP schedule..."
                  : "Saving schedule to project..."}
              </p>
              <Text type="secondary">
                MPP files are parsed on the server and may take a moment
              </Text>
            </div>
          ) : (
            <>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag a schedule file here</p>
              <p className="ant-upload-hint">MPP, XER</p>
            </>
          )}
        </Dragger>
      </div>

      <input
        ref={uploadRef}
        type="file"
        accept={acceptedTypes}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) processFile(file);
          event.target.value = "";
        }}
      />
    </>
  );
};

export default Schedule;
